import { useEffect, useRef } from 'react'

interface Star {
  x: number
  y: number
  vx: number
  vy: number
  r: number
  tw: number
  twSp: number
  glow: number
  glowV: number
}

/**
 * V3 · Gravity stars — the product page's background field, ported
 * from the standalone gravity-stars demo. Accent-coloured twinkling
 * dots drift on a 2D canvas; the cursor bends the field toward
 * itself and near stars glow with a spring response.
 *
 * Fills its nearest positioned ancestor and stays out of the way:
 * pointer-events none (the panel's controls sit on top of it), sized
 * to the container rather than the viewport, star count scaled to
 * the covered area, paused while offscreen or in a hidden tab, and a
 * single static frame under prefers-reduced-motion. Colour is read
 * from `--sss-accent`, so it follows the theme without a prop.
 */
export function GravityStars({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const MAX_SIZE = 2.5 // max star radius, px
    const OPACITY = 0.85
    const GLOW = 15
    const SPEED = 0.3
    const INFLUENCE = 100 // px radius around the cursor
    const STRENGTH = 75

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    let W = 0
    let H = 0
    let stars: Star[] = []
    const mouse = { x: -1e4, y: -1e4 }

    const starColor = () =>
      getComputedStyle(canvas).getPropertyValue('--sss-accent').trim() || '#EF6CB8'

    const makeStar = (): Star => {
      const a = Math.random() * Math.PI * 2
      const sp = (0.4 + Math.random() * 0.6) * SPEED
      return {
        x: Math.random() * W,
        y: Math.random() * H,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        r: (0.35 + Math.random() * 0.65) * MAX_SIZE,
        tw: Math.random() * Math.PI * 2,
        twSp: 0.5 + Math.random() * 1.5,
        glow: 0,
        glowV: 0,
      }
    }

    // ~0.55 stars per 10,000 px² — the demo's density on a laptop
    // viewport — capped so a tall panel doesn't fill with them.
    const seed = () => {
      const count = Math.min(Math.round((W * H) / 10000 * 0.55), 80)
      stars = Array.from({ length: count }, makeStar)
    }

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      W = rect.width
      H = rect.height
      canvas.width = W * dpr
      canvas.height = H * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      if (stars.length === 0) seed()
    }

    const applyMouse = (s: Star, dt: number) => {
      const dx = mouse.x - s.x
      const dy = mouse.y - s.y
      const d = Math.hypot(dx, dy)
      let target = 0
      if (d < INFLUENCE && d > 0.001) {
        const falloff = 1 - d / INFLUENCE
        target = falloff
        const f = STRENGTH * 0.00004 * falloff * dt
        s.vx += (dx / d) * f
        s.vy += (dy / d) * f
      }
      // spring glow response
      const k = 0.12
      const damp = 0.72
      s.glowV = (s.glowV + (target - s.glow) * k) * damp
      s.glow += s.glowV
    }

    // rigid bounce: separate, then swap velocity along the normal
    const collide = () => {
      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < stars.length; j++) {
          const a = stars[i]
          const b = stars[j]
          const dx = b.x - a.x
          const dy = b.y - a.y
          const d = Math.hypot(dx, dy)
          const minD = a.r + b.r
          if (d >= minD || d === 0) continue

          const nx = dx / d
          const ny = dy / d
          const overlap = (minD - d) / 2
          a.x -= nx * overlap
          a.y -= ny * overlap
          b.x += nx * overlap
          b.y += ny * overlap
          const avn = a.vx * nx + a.vy * ny
          const bvn = b.vx * nx + b.vy * ny
          const diff = bvn - avn
          a.vx += diff * nx
          a.vy += diff * ny
          b.vx -= diff * nx
          b.vy -= diff * ny
        }
      }
    }

    const step = (dt: number) => {
      const maxV = SPEED * 6
      for (const s of stars) {
        applyMouse(s, dt)
        const v = Math.hypot(s.vx, s.vy)
        if (v > maxV) {
          s.vx *= maxV / v
          s.vy *= maxV / v
        }
        s.x += s.vx * dt * 0.06
        s.y += s.vy * dt * 0.06
        s.tw += 0.016 * s.twSp
        // toroidal wrap
        const m = s.r + 2
        if (s.x < -m) s.x = W + m
        else if (s.x > W + m) s.x = -m
        if (s.y < -m) s.y = H + m
        else if (s.y > H + m) s.y = -m
      }
      collide()
    }

    const render = () => {
      ctx.clearRect(0, 0, W, H)
      const col = starColor()
      for (const s of stars) {
        const twinkle = 0.75 + 0.25 * Math.sin(s.tw)
        ctx.globalAlpha = Math.min(OPACITY * twinkle + s.glow * 0.25, 1)
        ctx.fillStyle = col
        ctx.shadowColor = col
        ctx.shadowBlur = GLOW * (0.5 + s.glow)
        ctx.beginPath()
        ctx.arc(s.x, s.y, s.r * (1 + s.glow * 0.5), 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
      ctx.shadowBlur = 0
    }

    resize()

    if (reduced) {
      render()
      const onResize = () => {
        resize()
        render()
      }
      window.addEventListener('resize', onResize)
      return () => window.removeEventListener('resize', onResize)
    }

    let raf = 0
    let last = performance.now()
    let visible = true

    const frame = (now: number) => {
      const dt = Math.min(now - last, 50)
      last = now
      if (visible && !document.hidden) {
        step(dt)
        render()
      }
      raf = requestAnimationFrame(frame)
    }

    const onMove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect()
      mouse.x = e.clientX - r.left
      mouse.y = e.clientY - r.top
    }
    const onOut = () => {
      mouse.x = -1e4
      mouse.y = -1e4
    }
    const onVisibility = () => {
      last = performance.now()
    }

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      last = performance.now()
    })
    io.observe(canvas)

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseout', onOut)
    document.addEventListener('visibilitychange', onVisibility)
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      io.disconnect()
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseout', onOut)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 size-full ${className ?? ''}`}
    />
  )
}
